/**
 * Staff sign-in: password against Better Auth, or a magic link for an email that has a staff role.
 */
import * as stylex from "@stylexjs/stylex";
import type { FormEvent, ReactNode } from "react";
import { useState } from "react";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Mark } from "@lattice/ui/Mark";
import { api } from "./api.ts";
import type { PasskeyOutcome } from "./passkey.ts";
import { registerPasskey, signInWithPasskey } from "./passkey.ts";
import { Button, ErrorText, Field } from "./ui.tsx";
import { refusal } from "./words.ts";
const PASSKEY_REFUSAL: Readonly<Record<Exclude<PasskeyOutcome, "ok">, string>> = {
  unsupported: "passkey_unsupported",
  refused: "passkey_refused",
  cancelled: "passkey_cancelled",
  failed: "passkey_failed"
};
export function Card({
  children
}: {
  readonly children: ReactNode;
}) {
  return <main className="x1ov3xa9 xrvj5dj x1ku5rj1 x3i8z2n x9f619">
      <div className="x9f619 xh8yej3 x1j9u4d2 x78zum5 xdt5ytf x15agyyk xq24xx7 xcqm4px xmkeg23 x1y0btm7 xtkjqb x1gzjth6">
        <div className="x78zum5 x6s0dn4 x1173whf x10dbofl x117lpx5 x1rf3zrh">
          <Mark size={20} />
          <span className="xzb3lse">Lattice</span>
          <span className="x1jty25b xox936i">Admin</span>
        </div>
        {children}
      </div>
    </main>;
}
export function CardCopy({
  title,
  children
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return <div className="x78zum5 xdt5ytf x1173whf">
      <h1 className="x1ghz6dp x3cmmws xqu410z x1d3j7mx x1e5flnj">{title}</h1>
      <p className="x1ghz6dp xl4dl4e x1jty25b xbz0q3o">{children}</p>
    </div>;
}
export function AddPasskey({
  label = "Add a passkey",
  fill = false
}: {
  readonly label?: string;
  readonly fill?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function add() {
    setBusy(true);
    setError(null);
    const outcome = await registerPasskey();
    setBusy(false);
    if (outcome === "ok") {
      setSaved(true);
      return;
    }
    setError(refusal(PASSKEY_REFUSAL[outcome]));
  }
  return <>
      <Button tone="quiet" fill={fill} disabled={busy} onClick={() => void add()}>
        {saved ? "Passkey saved" : label}
      </Button>
      {error === null ? null : <ErrorText>{error}</ErrorText>}
    </>;
}
export function SignIn() {
  const [email, setEmail] = useState("");
  const [secret, setSecret] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function withPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const response = await fetch("/api/auth/sign-in/email", {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({
        email: email.trim(),
        password: secret
      })
    });
    setBusy(false);
    if (response.ok) {
      window.location.assign("/");
      return;
    }
    setError("That email or password is wrong.");
  }
  async function sendLink() {
    setBusy(true);
    setError(null);
    const result = await api(ROUTES.staffSignIn, {
      body: {
        email
      }
    });
    setBusy(false);
    if (result.ok) setSentTo(email.trim());else setError(refusal(result.error));
  }
  async function passkey() {
    setBusy(true);
    setError(null);
    const outcome = await signInWithPasskey();
    setBusy(false);
    if (outcome === "ok") window.location.reload();else setError(refusal(PASSKEY_REFUSAL[outcome]));
  }
  if (sentTo !== null) return <Card>
        <CardCopy title="Check your email">
          If {sentTo} has a staff role, a sign-in link is on its way. It works once and expires in
          24 hours.
        </CardCopy>
        <Button tone="quiet" onClick={() => setSentTo(null)}>
          Use another email
        </Button>
      </Card>;
  return <Card>
      <CardCopy title="Sign in">Staff email and password. A link or passkey works too.</CardCopy>
      <form onSubmit={withPassword} className="x78zum5 xdt5ytf x1r0x45p">
        <Field id="email" label="Work email" type="email" value={email} onChange={setEmail} autoFocus />
        <Field id="password" label="Password" type="password" value={secret} onChange={setSecret} />
        {error === null ? null : <ErrorText>{error}</ErrorText>}
        <Button type="submit" disabled={busy || email.trim() === "" || secret === ""}>
          Sign in
        </Button>
        <Button tone="quiet" disabled={busy || email.trim() === ""} onClick={() => void sendLink()}>
          Email me a sign-in link
        </Button>
        <p className="x1ghz6dp x2b8uid xl4dl4e x1jty25b">or</p>
        <Button tone="quiet" disabled={busy} onClick={() => void passkey()}>
          Use a passkey
        </Button>
      </form>
    </Card>;
}